import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/useAuthStore.jsx';
import axios from 'axios';
import '@assets/css/auth/login.css'

export default function LoginSuccess() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuthStore();
    const [status, setStatus] = useState('로그인 처리 중...');
    const [error, setError] = useState(null);

    useEffect(() => {
        const processLoginResponse = async () => {
            try {
                console.log('🔐 [LoginSuccess] Processing login response...');
                console.log('🔐 [LoginSuccess] Current URL:', window.location.href);
                console.log('🔐 [LoginSuccess] Current cookies:', document.cookie);

                // URL 파라미터 확인
                const searchParams = new URLSearchParams(location.search);

                // 에러 파라미터가 있는지 확인
                const errorParam = searchParams.get('error');
                const errorMessage = searchParams.get('message');

                if (errorParam) {
                    console.error('❌ [LoginSuccess] OAuth error:', errorParam, errorMessage);
                    setError(errorMessage || '로그인 처리 중 오류가 발생했습니다.');
                    setTimeout(() => navigate(`/error?error=${errorParam}&message=${errorMessage}`), 1000);
                    return;
                }

                // URL 파라미터에서 토큰 확인 (cross-origin 리다이렉트 대응 - localdev 전용)
                const accessToken = searchParams.get('ab_at');
                const refreshToken = searchParams.get('ab_rt');
                const expiresIn = searchParams.get('expires_in');

                if (accessToken && refreshToken) {
                    console.log('🔐 [LoginSuccess] Tokens found in URL params (localdev mode)');
                    // 프론트엔드에서 쿠키 설정 (HttpOnly는 설정 불가, JavaScript로 접근 가능)
                    const accessMaxAge = expiresIn || 600; // 10분 기본값
                    const refreshMaxAge = 21600; // 6시간

                    // 쿠키 설정 (SameSite=Lax, Path=/)
                    document.cookie = `ab_at=${accessToken}; Max-Age=${accessMaxAge}; Path=/; SameSite=Lax`;
                    document.cookie = `ab_rt=${refreshToken}; Max-Age=${refreshMaxAge}; Path=/; SameSite=Lax`;

                    console.log('✅ [LoginSuccess] Cookies set from URL params');
                    console.log('🍪 [LoginSuccess] Updated cookies:', document.cookie);

                    // URL에서 토큰 파라미터 제거 (보안)
                    window.history.replaceState({}, '', '/loginSuccess');
                } else {
                    console.log('🔐 [LoginSuccess] No tokens in URL params (prod/dev mode - expecting HttpOnly cookies from backend)');
                }

                // 로그인 성공 후 쿠키가 설정되었으므로 사용자 정보를 가져옴
                fetchUserInfo();
            } catch (error) {
                console.error('❌ [LoginSuccess] Process login error:', error);
                setError('로그인 처리 중 예기치 않은 오류가 발생했습니다.');
            }
        };

        const fetchUserInfo = async () => {
            try {
                console.log('🔐 [LoginSuccess] Fetching user info...');
                console.log('🔐 [LoginSuccess] API URL:', import.meta.env.VITE_ADMIN_BACKEND_API_URL);
                console.log('🔐 [LoginSuccess] Current cookies:', document.cookie);

                // /api/v1/auth/me API를 호출하여 사용자 정보 가져오기
                const response = await axios.get(`${import.meta.env.VITE_ADMIN_BACKEND_API_URL}/auth/me`, {
                    withCredentials: true // 쿠키를 포함하여 요청
                });

                console.log('✅ [LoginSuccess] API response:', response.status, response.data);

                if (response.data?.success && response.data?.data) {
                    const userData = response.data.data;

                    // 인증 상태 업데이트 (토큰은 쿠키로 관리됨)
                    login(userData);

                    if (userData.isActive) {
                        if (userData.isApproved) {
                            // 성공 메시지 표시 후 메인 페이지로 이동
                            setStatus('로그인 성공! 메인 페이지로 이동합니다.');
                            setTimeout(() => navigate('/'), 1000);
                        } else {
                            // 가입 승인전! 메인 페이지로 이동합니다.
                            setStatus('가입 승인 전! 메인 페이지로 이동합니다.');
                            setTimeout(() => navigate('/loginPendingApproval'), 1000);
                        }
                    } else {
                        // 비활성화
                        setStatus('비활성화된 계정입니다.');
                        setTimeout(() => navigate('/loginFailure'), 1000);
                    }
                } else {
                    console.error('❌ [LoginSuccess] Invalid response structure:', response.data);
                    setError('사용자 정보를 가져올 수 없습니다.');
                }
            } catch (error) {
                console.error('❌ [LoginSuccess] Fetch user info error:', error);
                console.error('❌ [LoginSuccess] Error details:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status,
                    headers: error.response?.headers
                });
                console.error('❌ [LoginSuccess] Current cookies:', document.cookie);

                let errorMessage = '토큰 처리 중 오류가 발생했습니다.';
                if (error.response?.status === 401) {
                    errorMessage = '인증 토큰이 유효하지 않습니다. 쿠키가 전송되지 않았을 수 있습니다.';
                } else if (error.response?.status === 403) {
                    errorMessage = '접근 권한이 없습니다.';
                } else if (error.code === 'ERR_NETWORK') {
                    errorMessage = '네트워크 연결 오류가 발생했습니다.';
                }

                setError(errorMessage + ' (자세한 내용은 개발자 도구 Console 탭을 확인하세요)');
            }
        };

        processLoginResponse();
    }, [login, navigate, location]);

    // 오류가 있는 경우 로그인 페이지로 이동하는 함수
    const goToLogin = () => {
        navigate('/login');
    };

    // 오류가 있는 경우 오류 화면 표시
    if (error) {
        return (
            <div className="login-failure-container">
                <div className="login-failure-card">
                    <h2>로그인 실패</h2>
                    <div className="error-message">
                        <p>{error}</p>
                    </div>
                    <button onClick={goToLogin} className="login-button">
                        로그인 페이지로 이동
                    </button>
                </div>
            </div>
        );
    }

    // 정상 처리 중인 경우 로딩 화면 표시
    return (
        <div className="login-success-container">
            <div className="login-success-card">
                <h2>로그인</h2>
                <div className="status-message">
                    <p>{status}</p>
                </div>
                <div className="loading-spinner"></div>
            </div>
        </div>
    );
}